import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'

import { useUpdateTerminalOrganisation } from '@dhl/mdp-api'
import { BackButton } from '@dhl/components'
import { Box, Card, Grid } from '@dhl-ui/components'
import { links } from '~/AppRoutes'

import { useNotificationMessage } from '~/common'

import { AccountDataTable } from '~/common/components/accountnumbers'
import { getErrorMessage } from '~/common/util/axiosUtil'
import { TerminalForm, CancelDialog } from '~/modules/terminals/components'
import { useTerminal } from '~/modules/terminals/hooks'
import { createValidationSchema, convertToTerminalDto, type Terminal } from '~/modules/terminals/model'
import { useAddressService } from '~/common/components/addresses'

type Params = {
  id: string
}

export const EditTerminalPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<Params>()
  const { t } = useTranslation()
  const { showSuccess, showError } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { terminal, isLoading } = useTerminal(id)

  const { mutateAsync: updateTerminal } = useUpdateTerminalOrganisation()

  const { addressValidationSchema, updateAddressValidationSchema } = useAddressService(
    terminal?.data.address.countryCode
  )
  const validationSchema = createValidationSchema(t, addressValidationSchema)

  const onChangeCountry = async (countryCode: string) => {
    updateAddressValidationSchema(countryCode)
  }

  const onBack = (dirty: boolean) => (dirty && id ? setCancelDialogOpen(true) : navigate(links.terminalsLink()))

  const onSubmit = async (updatedTerminal: Terminal) => {
    const updatedTerminalDto = convertToTerminalDto(updatedTerminal)

    try {
      await updateTerminal({ id: updatedTerminal.id, data: updatedTerminalDto })
      showSuccess(t('admin.terminalsPage.editTerminalSuccess'))
      navigate(links.terminalsLink())
    } catch (error) {
      const message = getErrorMessage(error, t('admin.terminalsPage.editTerminalError'))
      showError(message)
    }
  }

  if (isLoading || !terminal) {
    return null
  }

  return (
    <>
      <Formik initialValues={terminal} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
        {({ dirty }) => (
          <>
            <Box marginBottom={2}>
              <BackButton onClick={() => onBack(dirty)}>
                {t('admin.terminalsPage.terminalForm.backButtonTitle')}
              </BackButton>
            </Box>
            <Grid container direction="row" spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Card>
                  <Card.Header title={t('admin.terminalsPage.terminalDetailsPage.titleText')} />
                  <Card.Content>
                    {terminal && (
                      <TerminalForm
                        country={terminal?.data.address.countryCode}
                        onChangeCountry={onChangeCountry}
                        onCancel={() => onBack(dirty)}
                      />
                    )}
                  </Card.Content>
                </Card>
              </Grid>
              <Grid size={{ xs: 6 }}>
                {terminal && <AccountDataTable organisation={terminal} businessType={'terminal'} />}
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
